import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Wallet,
  Receipt,
  Users,
  Building,
  Zap,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Loader2,
  CheckCircle,
  Clock,
  DollarSign,
  Banknote,
  FileText,
  Pencil,
  Trash2,
  Calendar,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Package,
  Truck,
  RotateCcw,
  Tag,
  Filter,
  Factory,
  Boxes,
  Hammer,
  ShoppingCart,
  Upload,
  Download,
  FileSpreadsheet,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../components/ui/alert-dialog';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-LK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const accountingPathways = [
  { value: 'quick_expense', label: 'Quick Expense', route: '/quick-transactions' },
  { value: 'inventory_procurement', label: 'Purchase Order & GRN', route: '/purchase-orders' },
  { value: 'fixed_asset', label: 'Fixed Asset Acquisition', route: '/fixed-assets/acquisitions' },
  { value: 'fixed_asset_improvement', label: 'Fixed Asset Improvement', route: '/fixed-assets/register' },
  { value: 'prepayment', label: 'Prepaid Expense / Accrual Review', route: '/accounting' },
  { value: 'payroll', label: 'Payroll', route: '/payroll' },
  { value: 'director_funding', label: 'Director/Owner Funding Review', route: '/accounting' },
  { value: 'manual_journal', label: 'Controlled Manual Journal', route: '/accounting' },
];

const pathwayLabel = (value) => accountingPathways.find((item) => item.value === value)?.label || value;
const pathwayRoute = (value, suggestion) => suggestion?.action_route || accountingPathways.find((item) => item.value === value)?.route || '/accounting';

export default function QuickTransactions() {
  const [investors, setInvestors] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 15, total: 0, total_pages: 0 });
  const [transactionFilter, setTransactionFilter] = useState('active');
  const [transactionSearch, setTransactionSearch] = useState('');
  const transactionRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [transactionToCorrect, setTransactionToCorrect] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({ date: '', description: '', reference_number: '', notes: '', reason: '' });
  
  const [activeModal, setActiveModal] = useState(null);
  
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Colombo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  };
  
  // Form states
  const [expenseForm, setExpenseForm] = useState({
    expense_type: '',
    expense_account_code: '',
    description: '',
    amount: '',
    vendor: '',
    bank_account_id: '',
    payment_method: 'bank',
    reference: '',
    notes: '',
    date: getTodayDate(),
    suggestion_id: '',
    accounting_pathway: 'quick_expense',
    override_reason: '',
    allow_duplicate: false,
    client_request_id: ''
  });
  
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [expenseSuggestion, setExpenseSuggestion] = useState(null);
  const [suggestingExpense, setSuggestingExpense] = useState(false);
  
  const [salaryForm, setSalaryForm] = useState({
    employee_name: '',
    amount: '',
    month: '',
    allowances: '0',
    deductions: '0',
    bank_account_id: '',
    payment_method: 'bank',
    notes: '',
    date: getTodayDate()
  });
  
  const [revenueForm, setRevenueForm] = useState({
    revenue_type: 'sales',
    description: '',
    amount: '',
    customer: '',
    bank_account_id: '',
    payment_method: 'bank',
    reference: '',
    notes: '',
    date: getTodayDate()
  });
  
  const [loanForm, setLoanForm] = useState({
    transaction_type: 'receive',
    loan_type: 'bank_loan',
    lender_name: '',
    amount: '',
    interest_amount: '0',
    bank_account_id: '',
    reference: '',
    notes: '',
    date: getTodayDate()
  });

  // Excel Upload States
  const [excelUploadOpen, setExcelUploadOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState('upload'); // upload, preview, importing, success
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchCurrentUser();
    fetchBankAccounts();
    fetchExpenseAccounts();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await api.get('/bank-accounts');
      setBankAccounts((response.data || [])
        .filter((account) => ['bank', 'cash'].includes(String(account.account_type || '').toLowerCase()))
        .sort((a, b) => {
          const aType = String(a.account_type || '').toLowerCase();
          const bType = String(b.account_type || '').toLowerCase();
          if (aType !== bType) return aType === 'cash' ? -1 : 1;
          return String(a.account_name || '').localeCompare(String(b.account_name || ''));
        }));
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    }
  };

  const fetchExpenseAccounts = async () => {
    try {
      const response = await api.get('/simple-finance/expense-accounts');
      setExpenseAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch expense accounts:', error);
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  const fetchData = async () => {
    try {
      setLoading(true);
      const [investorsRes] = await Promise.all([
        api.get('/simple-finance/investors')
      ]);
      setInvestors(investorsRes.data);
      await fetchTransactions(1);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1, filter = transactionFilter, search = transactionSearch) => {
    const request = ++transactionRequest.current;
    try {
      const response = await api.get('/simple-finance/all-transactions', {
        params: { page, per_page: 15, transaction_type: filter, search }
      });
      if (request !== transactionRequest.current) return;
      setAllTransactions(response.data.transactions);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      if (request === transactionRequest.current) {
        setAllTransactions([]);
        toast.error('Could not load matching transactions. Please retry.');
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchTransactions(newPage);
    }
  };

  const handleFilterChange = (newFilter) => {
    setTransactionFilter(newFilter);
  };

  useEffect(() => {
    const timer = setTimeout(
      () => fetchTransactions(1, transactionFilter, transactionSearch), 300
    );
    return () => {
      clearTimeout(timer);
      transactionRequest.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionSearch, transactionFilter]);

  const resetExpenseForm = () => {
    setExpenseForm({
      expense_type: '', expense_account_code: '', description: '', amount: '', vendor: '',
      bank_account_id: '', payment_method: 'bank', reference: '', notes: '', date: getTodayDate(),
      suggestion_id: '', accounting_pathway: 'quick_expense', override_reason: '', allow_duplicate: false,
      client_request_id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`
    });
    setExpenseSuggestion(null);
  };

  const handleSuggestExpense = async () => {
    if (!expenseForm.description || expenseForm.description.trim().length < 3) {
      toast.error('Describe what you purchased first');
      return null;
    }
    setSuggestingExpense(true);
    try {
      const response = await api.post('/finance-assistant/suggest', {
        description: expenseForm.description.trim(),
        amount: parseFloat(expenseForm.amount) || 0,
        vendor: expenseForm.vendor || '',
        transaction_date: expenseForm.date,
        bank_account_id: expenseForm.bank_account_id || null
      });
      const suggestion = response.data;
      setExpenseSuggestion(suggestion);
      setExpenseForm((prev) => ({
        ...prev,
        suggestion_id: suggestion.id,
        accounting_pathway: suggestion.pathway,
        expense_type: suggestion.recommended_expense_type || prev.expense_type || 'other',
        expense_account_code: suggestion.recommended_account_code || prev.expense_account_code,
        override_reason: ''
      }));
      return suggestion;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate accounting guidance');
      return null;
    } finally {
      setSuggestingExpense(false);
    }
  };

  const acceptAlternative = (alternative) => {
    setExpenseForm((prev) => ({
      ...prev,
      accounting_pathway: alternative.pathway,
      expense_account_code: alternative.account_code || prev.expense_account_code,
      override_reason: alternative.pathway === expenseSuggestion?.pathway ? '' : `Selected alternative: ${alternative.reason || alternative.pathway}`
    }));
  };

  const openSelectedPathway = async () => {
    if (!expenseSuggestion) return;
    const selectedPathway = expenseForm.accounting_pathway;
    const changed = selectedPathway !== expenseSuggestion.pathway;
    if (changed && !expenseForm.override_reason.trim()) {
      toast.error('Explain why the selected pathway is more appropriate before continuing');
      return;
    }
    try {
      await api.post(`/finance-assistant/suggestions/${expenseSuggestion.id}/decision`, {
        accepted: !changed,
        selected_account_code: expenseForm.expense_account_code || null,
        selected_pathway: selectedPathway,
        override_reason: changed ? expenseForm.override_reason : null,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the accounting decision');
      return;
    }
    const route = pathwayRoute(selectedPathway, selectedPathway === expenseSuggestion.pathway ? expenseSuggestion : null);
    setActiveModal(null);
    window.location.assign(route);
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseSuggestion) {
      const suggestion = await handleSuggestExpense();
      if (suggestion) toast.info('Review the recommendation and confirm before posting');
      return;
    }
    if (expenseForm.accounting_pathway !== 'quick_expense') {
      await openSelectedPathway();
      return;
    }
    if (expenseSuggestion.pathway !== 'quick_expense' && !expenseForm.override_reason.trim()) {
      toast.error(`The assistant recommends ${expenseSuggestion.pathway_label}. Explain why Quick Expense is correct before overriding it.`);
      return;
    }
    const recommendedCode = expenseSuggestion.recommended_account_code;
    if (recommendedCode && expenseForm.expense_account_code !== recommendedCode && !expenseForm.override_reason.trim()) {
      toast.error(`Explain why account ${expenseForm.expense_account_code || 'none'} is better than recommended account ${recommendedCode}`);
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/simple-finance/expense-payment', {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        client_request_id: expenseForm.client_request_id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`)
      });
      toast.success(response.data.message);
      if (response.data.review_status === 'pending') {
        toast.info('Submitted for finance approval. It will not affect the ledger or reports until approved.');
      }
      setActiveModal(null);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record expense');
    } finally {
      setSubmitting(false);
    }
  };


  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.post('/simple-finance/salary-payment', {
        ...salaryForm,
        amount: parseFloat(salaryForm.amount),
        allowances: parseFloat(salaryForm.allowances) || 0,
        deductions: parseFloat(salaryForm.deductions) || 0
      });
      toast.success(response.data.message);
      setActiveModal(null);
      setSalaryForm({ employee_name: '', amount: '', month: '', allowances: '0', deductions: '0', bank_account_id: '', payment_method: 'bank', notes: '', date: getTodayDate() });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record salary');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevenueSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.post('/simple-finance/revenue-receipt', {
        ...revenueForm,
        amount: parseFloat(revenueForm.amount)
      });
      toast.success(response.data.message);
      setActiveModal(null);
      setRevenueForm({ revenue_type: 'sales', description: '', amount: '', customer: '', bank_account_id: '', payment_method: 'bank', reference: '', notes: '', date: getTodayDate() });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record revenue');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.post('/simple-finance/loan-transaction', {
        ...loanForm,
        amount: parseFloat(loanForm.amount),
        interest_amount: parseFloat(loanForm.interest_amount) || 0
      });
      toast.success(response.data.message);
      setActiveModal(null);
      setLoanForm({ transaction_type: 'receive', loan_type: 'bank_loan', lender_name: '', amount: '', interest_amount: '0', bank_account_id: '', reference: '', notes: '', date: getTodayDate() });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record loan transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    setSubmitting(true);
    try {
      await api.delete(`/simple-finance/transaction/${transactionToDelete.id}`);
      toast.success('Transaction reversed successfully');
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const openCorrection = (tx) => {
    setTransactionToCorrect(tx);
    setCorrectionForm({
      date: String(tx.date || tx.created_at || '').slice(0, 10),
      description: tx.description || '',
      reference_number: tx.reference || '',
      notes: tx.notes || '',
      reason: ''
    });
    setCorrectionOpen(true);
  };

  const submitCorrection = async () => {
    if (!transactionToCorrect || !correctionForm.reason.trim()) {
      toast.error('Reason for correction is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/simple-finance/transactions/${transactionToCorrect.id}/amendments`, correctionForm);
      toast.success('Correction request sent for approval');
      setCorrectionOpen(false);
      setTransactionToCorrect(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request correction');
    } finally {
      setSubmitting(false);
    }
  };

  // Excel Upload Handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/simple-finance/expense-template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'expense_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload an .xlsx file');
      return;
    }
    
    setUploadedFile(file);
    setUploadStep('validating');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/simple-finance/expense-upload/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setValidationResult(response.data);
      setUploadStep('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate file');
      setUploadStep('upload');
      setUploadedFile(null);
    }
  };

  const handleEditRow = (rowIndex) => {
    setEditingRow(rowIndex);
  };

  const handleUpdateRow = (rowIndex, field, value) => {
    const updatedRows = [...validationResult.rows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [field]: value
    };
    
    // Re-validate the row
    const row = updatedRows[rowIndex];
    const errors = [];
    
    if (!row.expense_type) errors.push("Expense Type is required");
    if (!row.description) errors.push("Description is required");
    if (!row.amount || row.amount <= 0) errors.push("Valid amount is required");
    if (!row.bank_account_id) errors.push("Pay From is required");
    if (!row.date) errors.push("Date is required");
    if (row.date && row.date > getTodayDate()) errors.push("Date cannot be in the future");
    
    updatedRows[rowIndex].errors = errors;
    updatedRows[rowIndex].is_valid = errors.length === 0;
    
    setValidationResult({
      ...validationResult,
      rows: updatedRows,
      valid_rows: updatedRows.filter(r => r.is_valid).length,
      invalid_rows: updatedRows.filter(r => !r.is_valid).length,
      can_import: updatedRows.every(r => r.is_valid)
    });
  };

  const handleRemoveRow = (rowIndex) => {
    const updatedRows = validationResult.rows.filter((_, idx) => idx !== rowIndex);
    setValidationResult({
      ...validationResult,
      rows: updatedRows,
      total_rows: updatedRows.length,
      valid_rows: updatedRows.filter(r => r.is_valid).length,
      invalid_rows: updatedRows.filter(r => !r.is_valid).length,
      can_import: updatedRows.length > 0 && updatedRows.every(r => r.is_valid)
    });
  };

  const handleImportExpenses = async () => {
    if (!validationResult?.can_import) {
      toast.error('Please fix all errors before importing');
      return;
    }
    
    setImporting(true);
    setUploadStep('importing');
    
    try {
      const response = await api.post('/simple-finance/expense-upload/import', {
        expenses: validationResult.rows.map(row => ({
          row_number: row.row_number,
          expense_type: row.expense_type,
          expense_account_code: row.expense_account_code,
          description: row.description,
          amount: row.amount,
          vendor: row.vendor,
          bank_account_id: row.bank_account_id,
          payment_method: row.payment_method || 'bank',
          reference: row.reference,
          notes: row.notes,
          date: row.date
        }))
      });
      
      setImportResult(response.data);
      setUploadStep('success');
      toast.success(`Successfully imported ${response.data.imported_count} transactions`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setUploadStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const resetExcelUpload = () => {
    setUploadStep('upload');
    setUploadedFile(null);
    setValidationResult(null);
    setEditingRow(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeExcelUpload = () => {
    setExcelUploadOpen(false);
    resetExcelUpload();
  };

  const expenseTypes = [
    // Operations & Facilities
    { value: 'utilities', label: 'Utilities (Electricity, Water, Gas)', icon: '💡' },
    { value: 'rent', label: 'Rent Payment', icon: '🏢' },
    { value: 'maintenance', label: 'Repairs & Maintenance', icon: '🔧' },
    
    // Digital & Technology
    { value: 'hosting', label: 'Web Hosting & Servers', icon: '🌐' },
    { value: 'domains', label: 'Domain Names', icon: '🔗' },
    { value: 'software', label: 'Software & Subscriptions', icon: '💻' },
    { value: 'it_services', label: 'IT Services & Support', icon: '🖥️' },
    
    // Purchases & Inventory
    { value: 'materials', label: 'Raw Materials & Supplies', icon: '📦' },
    { value: 'inventory', label: 'Inventory Purchase', icon: '🛒' },
    { value: 'equipment', label: 'Equipment & Tools', icon: '🔨' },
    { value: 'office_supplies', label: 'Office Supplies', icon: '📎' },
    
    // Marketing & Communication
    { value: 'marketing', label: 'Marketing & Advertising', icon: '📢' },
    { value: 'communication', label: 'Phone & Internet', icon: '📱' },
    
    // Professional & Business
    { value: 'professional_fees', label: 'Professional Fees (Legal, Accounting)', icon: '👔' },
    { value: 'insurance', label: 'Insurance', icon: '🛡️' },
    { value: 'licenses', label: 'Licenses & Permits', icon: '📜' },
    { value: 'bank_charges', label: 'Bank Charges & Fees', icon: '🏦' },
    
    // Travel & Transport
    { value: 'transport', label: 'Transport & Delivery', icon: '🚗' },
    { value: 'travel', label: 'Business Travel', icon: '✈️' },
    { value: 'fuel', label: 'Fuel & Vehicle Expenses', icon: '⛽' },
    
    // Other
    { value: 'training', label: 'Training & Education', icon: '📚' },
    { value: 'meals', label: 'Meals & Entertainment', icon: '🍽️' },
    { value: 'other', label: 'Other Expenses', icon: '📋' }
  ];

  const revenueTypes = [
    { value: 'sales', label: 'Sales Revenue' },
    { value: 'service', label: 'Service Income' },
    { value: 'interest', label: 'Interest Income' },
    { value: 'commission', label: 'Commission Income' },
    { value: 'other', label: 'Other Income' }
  ];

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'capital_investment': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'capital_withdrawal': return <TrendingDown className="w-4 h-4 text-amber-600" />;
      case 'expense_payment': return <Receipt className="w-4 h-4 text-red-600" />;
      case 'salary_payment': return <Users className="w-4 h-4 text-blue-600" />;
      case 'revenue_receipt': return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'loan_received': return <Banknote className="w-4 h-4 text-purple-600" />;
      case 'loan_repayment': return <CreditCard className="w-4 h-4 text-orange-600" />;
      case 'po_payment': return <Package className="w-4 h-4 text-indigo-600" />;
      case 'so_payment': 
      case 'sales_payment': return <DollarSign className="w-4 h-4 text-emerald-600" />;
      case 'grn_return': return <RotateCcw className="w-4 h-4 text-amber-600" />;
      case 'additional_charge': return <Truck className="w-4 h-4 text-slate-600" />;
      case 'discount_received': return <Tag className="w-4 h-4 text-teal-600" />;
      case 'mfg_material_issue': return <Boxes className="w-4 h-4 text-blue-600" />;
      case 'mfg_labor': return <Users className="w-4 h-4 text-amber-600" />;
      case 'mfg_production': return <Factory className="w-4 h-4 text-green-600" />;
      case 'mfg_scrap': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'mfg_reversal': return <RotateCcw className="w-4 h-4 text-slate-600" />;
      case 'manufacturing': return <Hammer className="w-4 h-4 text-indigo-600" />;
      case 'raw_material_purchase': return <Boxes className="w-4 h-4 text-orange-600" />;
      case 'woo_sale': return <ShoppingCart className="w-4 h-4 text-emerald-600" />;
      case 'woo_cogs': return <Package className="w-4 h-4 text-rose-600" />;
      default: return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'capital_investment': 
      case 'revenue_receipt':
      case 'so_payment':
      case 'sales_payment':
      case 'discount_received':
      case 'mfg_production':
      case 'woo_sale':
        return 'bg-green-50 border-green-200';
      case 'grn_return':
        return 'bg-amber-50 border-amber-200';
      case 'capital_withdrawal':
      case 'expense_payment':
      case 'salary_payment':
      case 'loan_repayment':
      case 'po_payment':
      case 'mfg_scrap':
      case 'raw_material_purchase':
      case 'woo_cogs':
        return 'bg-red-50 border-red-200';
      case 'additional_charge':
      case 'mfg_reversal':
        return 'bg-slate-100 border-slate-300';
      case 'mfg_material_issue':
      case 'mfg_labor':
      case 'manufacturing':
        return 'bg-indigo-50 border-indigo-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'capital_investment': return 'Capital Investment';
      case 'capital_withdrawal': return 'Capital Withdrawal';
      case 'expense_payment': return 'Expense';
      case 'salary_payment': return 'Salary';
      case 'revenue_receipt': return 'Revenue';
      case 'loan_received': return 'Loan Received';
      case 'loan_repayment': return 'Loan Repayment';
      case 'po_payment': return 'PO Payment';
      case 'so_payment': 
      case 'sales_payment': return 'Sales Payment';
      case 'grn_return': return 'GRN Return';
      case 'additional_charge': return 'Charge';
      case 'discount_received': return 'Discount';
      case 'mfg_material_issue': return 'Material Issue';
      case 'mfg_labor': return 'Labor Cost';
      case 'mfg_production': return 'Production';
      case 'mfg_scrap': return 'Scrap/Reject';
      case 'mfg_reversal': return 'MFG Reversal';
      case 'manufacturing': return 'Manufacturing';
      case 'raw_material_purchase': return 'RM Purchase';
      case 'woo_sale': return 'WooCommerce Sale';
      case 'woo_cogs': return 'WooCommerce COGS';
      default: return String(type || 'Journal Entry').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }
  };

  const quickActions = [
    {
      id: 'expense',
      title: 'Pay Expense',
      description: 'Utilities, rent, supplies, etc.',
      icon: Receipt,
      color: 'bg-red-500',
      onClick: () => setActiveModal('expense')
    },
    {
      id: 'salary',
      title: 'Pay Salary',
      description: 'Employee salary payment',
      icon: Users,
      color: 'bg-blue-500',
      onClick: () => setActiveModal('salary')
    },
    {
      id: 'revenue',
      title: 'Receive Payment',
      description: 'Sales, service income, etc.',
      icon: DollarSign,
      color: 'bg-green-500',
      onClick: () => setActiveModal('revenue')
    },
    {
      id: 'loan',
      title: 'Loan Transaction',
      description: 'Receive or repay loan',
      icon: Banknote,
      color: 'bg-purple-500',
      onClick: () => setActiveModal('loan')
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Quick Transactions</h1>
        <p className="text-slate-500 mt-1">Record financial transactions easily - no accounting knowledge needed!</p>
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Card 
            key={action.id}
            className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-indigo-300"
            onClick={action.onClick}
          >
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center`}>
                  <action.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{action.title}</h3>
                  <p className="text-sm text-slate-500">{action.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bulk Import Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Bulk Import Expenses</h3>
                <p className="text-sm text-slate-600">
                  Upload an Excel file to import multiple expense transactions at once
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700" 
                size="sm" 
                onClick={() => setExcelUploadOpen(true)}
                data-testid="upload-excel-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
              <Zap className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Automatic Double-Entry</h3>
              <p className="text-sm text-slate-600">
                All transactions automatically create proper journal entries. No need to know debits and credits!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Transactions
            </CardTitle>
            <Input
              aria-label="Search recent transactions"
              placeholder="Search description, reference, amount..."
              className="max-w-sm mx-3"
              value={transactionSearch}
              onChange={(event) => setTransactionSearch(event.target.value)}
            />
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={transactionFilter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active Transactions</SelectItem>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="corrected">Corrected Replacements</SelectItem>
                  <SelectItem value="reversed">Reversed History</SelectItem>
                  <SelectItem value="expense_payment">Expenses</SelectItem>
                  <SelectItem value="salary_payment">Salaries</SelectItem>
                  <SelectItem value="revenue_receipt">Revenue</SelectItem>
                  <SelectItem value="po_payment">PO Payments</SelectItem>
                  <SelectItem value="sales_payment">Sales Payments</SelectItem>
                  <SelectItem value="woo_sale">WooCommerce Sales</SelectItem>
                  <SelectItem value="woo_cogs">WooCommerce COGS</SelectItem>
                  <SelectItem value="grn_return">GRN Returns</SelectItem>
                  <SelectItem value="additional_charge">Charges</SelectItem>
                  <SelectItem value="discount_received">Discounts</SelectItem>
                  <SelectItem value="loan_received">Loans</SelectItem>
                  <SelectItem value="capital_investment">Capital</SelectItem>
                  <SelectItem value="manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="fixed_assets">Fixed Assets</SelectItem>
                  <SelectItem value="journal_entry">All Journal Entries</SelectItem>
                  <SelectItem value="manual_journal">Manual Journals</SelectItem>
                  <SelectItem value="opening_balance">Opening Balances</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {isAdmin && (
            <p className="text-xs text-slate-500">Quick-transaction corrections require approval. Admins may also reverse a transaction.</p>
          )}
        </CardHeader>
        <CardContent>
          {allTransactions.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No transactions found</p>
              <p className="text-sm text-slate-400">Use the quick actions above to record your first transaction</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allTransactions.map((tx) => (
                <div 
                  key={tx.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${getTransactionColor(tx.transaction_type)}`}
                >
                  <div className="flex items-center gap-3">
                    {getTransactionIcon(tx.transaction_type)}
                    <div>
                      <p className="font-medium text-slate-900 line-clamp-1">{tx.description}</p>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(tx.date || tx.created_at)}
                        {tx.reference && (
                          <span className="text-xs text-slate-400">({tx.reference})</span>
                        )}
                        {tx.source && <span className="text-xs text-slate-400">· {tx.source}</span>}
                        {tx.posting_status === 'reversed' && <Badge variant="outline" className="border-red-300 text-red-700">Reversed</Badge>}
                        {tx.posting_status === 'reversal' && <Badge variant="outline" className="border-red-300 text-red-700">Reversal</Badge>}
                        {tx.posting_status === 'corrected' && <Badge variant="outline" className="border-emerald-300 text-emerald-700">Corrected</Badge>}
                        {tx.posting_status === 'unposted_exception' && <Badge variant="outline" className="border-amber-300 text-amber-700">No journal</Badge>}
                        {tx.amendment_status === 'pending' && <Badge variant="outline" className="border-blue-300 text-blue-700">Correction Pending</Badge>}
                        {tx.amendment_status === 'rejected' && <Badge variant="outline" className="border-slate-300 text-slate-700">Correction Rejected</Badge>}
                        {tx.amendment_status === 'correction_required' && <Badge variant="outline" className="border-amber-300 text-amber-700">Correction Needs Update</Badge>}
                        {tx.amendment_status === 'processing' && <Badge variant="outline" className="border-violet-300 text-violet-700">Correction Processing</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`font-semibold ${
                        tx.cash_direction === 'in' ? 'text-green-600' : tx.cash_direction === 'out' ? 'text-red-600' : 'text-slate-700'
                      }`}>
                        {tx.cash_direction === 'in' ? '+' : tx.cash_direction === 'out' ? '-' : ''}
                        {formatCurrency(tx.amount)}
                      </p>
                      <Badge variant="outline" className="text-xs">
                        {getTransactionLabel(tx.transaction_type)}
                      </Badge>
                    </div>
                    {tx.category === 'quick_transaction' && ['posted', 'corrected'].includes(tx.posting_status) && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="icon"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openCorrection(tx)}
                          disabled={['pending', 'approved', 'processing'].includes(tx.amendment_status)}
                          title="Request correction"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => { setTransactionToDelete(tx); setDeleteDialogOpen(true); }}
                            data-testid={`delete-transaction-${tx.id}`}
                            title="Reverse transaction"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.has_prev}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.has_next}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guided Expense Payment Modal */}
      <Dialog open={activeModal === 'expense'} onOpenChange={(open) => { if (!open) { setActiveModal(null); resetExpenseForm(); } }}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-red-600" />
              Guided Expense Entry
            </DialogTitle>
            <DialogDescription>
              Describe the purchase first. The ERP recommends the correct module, account, and double entry before you confirm.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleExpenseSubmit}>
            <div className="grid gap-5 py-4">
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                <div className="flex items-start gap-3">
                  <Zap className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
                  <div className="flex-1">
                    <p className="font-semibold text-indigo-950">Accounting assistant</p>
                    <p className="mt-1 text-sm text-indigo-800">Example: “Purchase one A4 paper bundle for office printing.” The system will normally recommend Office Supplies, but will warn you to use PO/GRN if the item is inventory or production material.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>What did the business purchase? *</Label>
                <Textarea
                  value={expenseForm.description}
                  onChange={(e) => { setExpenseForm({ ...expenseForm, description: e.target.value, suggestion_id: '' }); setExpenseSuggestion(null); }}
                  placeholder="e.g., Purchased one A4 paper bundle for internal office printing"
                  rows={3}
                  required
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2"><Label>Amount (LKR) *</Label><Input type="number" min="0" step="0.01" value={expenseForm.amount} onChange={(e) => { setExpenseForm({ ...expenseForm, amount: e.target.value, suggestion_id: '' }); setExpenseSuggestion(null); }} placeholder="0.00" required /></div>
                <div className="space-y-2"><Label>Vendor / Payee</Label><Input value={expenseForm.vendor} onChange={(e) => { setExpenseForm({ ...expenseForm, vendor: e.target.value, suggestion_id: '' }); setExpenseSuggestion(null); }} placeholder="Supplier or shop" /></div>
                <div className="space-y-2"><Label>Transaction Date *</Label><Input type="date" max={getTodayDate()} value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} required /></div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Pay From Account *</Label>
                  <Select value={expenseForm.bank_account_id} onValueChange={(value) => { const account = bankAccounts.find((item) => item.id === value); setExpenseForm({ ...expenseForm, bank_account_id: value, payment_method: account?.account_type === 'cash' ? 'cash' : 'bank', suggestion_id: '' }); setExpenseSuggestion(null); }}>
                    <SelectTrigger><SelectValue placeholder="Select business bank or cash" /></SelectTrigger>
                    <SelectContent>{bankAccounts.map((acc) => <SelectItem key={acc.id} value={acc.id}>{acc.account_name} ({formatCurrency(acc.current_balance)})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end"><Button type="button" onClick={handleSuggestExpense} disabled={suggestingExpense} className="w-full bg-indigo-600 hover:bg-indigo-700">{suggestingExpense ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}Suggest Accounting Pathway</Button></div>
              </div>

              {expenseSuggestion && (
                <div className={`rounded-xl border p-4 ${expenseSuggestion.pathway === 'quick_expense' ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2"><Badge className={expenseSuggestion.pathway === 'quick_expense' ? 'bg-emerald-600' : 'bg-amber-600'}>{expenseSuggestion.pathway_label}</Badge><Badge variant="outline">Confidence {Math.round((expenseSuggestion.confidence || 0) * 100)}%</Badge></div>
                      <h3 className="mt-3 font-semibold text-slate-950">{expenseSuggestion.classification}</h3>
                      <p className="mt-1 text-sm text-slate-700">{expenseSuggestion.reason}</p>
                      {expenseSuggestion.clarification_question && <p className="mt-2 rounded-lg bg-white/70 p-2 text-sm font-medium text-slate-800">Check: {expenseSuggestion.clarification_question}</p>}
                    </div>
                    {expenseSuggestion.recommended_account && <div className="min-w-[220px] rounded-lg bg-white p-3 text-sm shadow-sm"><p className="text-xs uppercase tracking-wide text-slate-500">Recommended debit</p><p className="mt-1 font-semibold">{expenseSuggestion.recommended_account.code} — {expenseSuggestion.recommended_account.name}</p><p className="mt-1 text-xs text-slate-500">{String(expenseSuggestion.recommended_account.report_group || '').replace(/_/g, ' ')}</p></div>}
                  </div>
                  {expenseSuggestion.journal_preview?.length > 0 && <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white"><div className="border-b bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">Journal preview</div>{expenseSuggestion.journal_preview.map((line, index) => <div key={`${line.side}-${index}`} className="flex items-center justify-between border-b px-3 py-2 text-sm last:border-b-0"><span><strong className="uppercase">{line.side}</strong> {line.account_code ? `${line.account_code} — ` : ''}{line.account_name}</span><span className="font-semibold">{formatCurrency(line.amount)}</span></div>)}</div>}
                  {expenseSuggestion.alternatives?.length > 0 && <div className="mt-3"><p className="text-xs font-bold uppercase tracking-wide text-slate-500">Alternative if the facts are different</p><div className="mt-2 flex flex-wrap gap-2">{expenseSuggestion.alternatives.map((alternative, index) => <Button key={`${alternative.pathway}-${index}`} type="button" variant="outline" size="sm" onClick={() => acceptAlternative(alternative)}>{alternative.pathway_label}{alternative.account_code ? ` (${alternative.account_code})` : ''}</Button>)}</div></div>}
                </div>
              )}

              {expenseSuggestion && (
                <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="space-y-2">
                    <Label>Confirmed accounting pathway *</Label>
                    <Select value={expenseForm.accounting_pathway} onValueChange={(value) => setExpenseForm({ ...expenseForm, accounting_pathway: value, override_reason: value === expenseSuggestion.pathway ? '' : expenseForm.override_reason })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{accountingPathways.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Select a different pathway only when the actual business purpose differs from the recommendation.</p>
                  </div>
                  {(expenseSuggestion.workflow_steps || []).length > 0 && expenseForm.accounting_pathway === expenseSuggestion.pathway && (
                    <div><p className="text-xs font-bold uppercase tracking-wide text-slate-500">Recommended workflow</p><ol className="mt-2 space-y-1 text-sm text-slate-700">{expenseSuggestion.workflow_steps.map((step, index) => <li key={step} className="flex gap-2"><span className="font-semibold text-indigo-600">{index + 1}.</span><span>{step}</span></li>)}</ol></div>
                  )}
                  {expenseForm.accounting_pathway !== 'quick_expense' && (
                    <div className="rounded-lg border border-indigo-200 bg-white p-3 text-sm text-indigo-900">
                      This entry will not be posted as a Quick Expense. Confirm to open <strong>{pathwayLabel(expenseForm.accounting_pathway)}</strong> and continue through the correct operational workflow.
                    </div>
                  )}
                </div>
              )}

              {expenseForm.accounting_pathway === 'quick_expense' && <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Expense Type *</Label>
                  <Select value={expenseForm.expense_type} onValueChange={(value) => setExpenseForm({ ...expenseForm, expense_type: value })}><SelectTrigger><SelectValue placeholder="Select expense type" /></SelectTrigger><SelectContent>{expenseTypes.map((type) => <SelectItem key={type.value} value={type.value}><span className="flex items-center gap-2"><span>{type.icon}</span>{type.label}</span></SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2">
                  <Label>Record To Account *</Label>
                  <Select value={expenseForm.expense_account_code} onValueChange={(value) => setExpenseForm({ ...expenseForm, expense_account_code: value })}>
                    <SelectTrigger><SelectValue placeholder="Select posting account" /></SelectTrigger>
                    <SelectContent className="max-h-[400px]">{expenseAccounts.length > 0 ? expenseAccounts.map((acc) => acc.is_header ? <div key={acc.code} className="mt-1 cursor-default select-none border-y border-slate-200 bg-slate-100 px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">{acc.code} - {acc.name}</div> : <SelectItem key={acc.code} value={acc.code} className="pl-6">› {acc.code} - {acc.name}{acc.report_group ? ` · ${String(acc.report_group).replace(/_/g, ' ')}` : ''}</SelectItem>) : <SelectItem value="__loading__" disabled>Loading accounts…</SelectItem>}</SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Header and protected control accounts cannot be posted through Quick Transactions.</p>
                </div>
              </div>}

              {expenseSuggestion && (expenseForm.accounting_pathway !== expenseSuggestion.pathway || (expenseForm.accounting_pathway === 'quick_expense' && expenseSuggestion.recommended_account_code && expenseForm.expense_account_code !== expenseSuggestion.recommended_account_code)) && <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3"><Label>Reason for overriding the recommendation *</Label><Textarea value={expenseForm.override_reason} onChange={(e) => setExpenseForm({ ...expenseForm, override_reason: e.target.value })} placeholder="Explain the business facts that make your selected pathway/account correct" rows={2} /><p className="text-xs text-amber-800">Overrides are saved to the audit trail and help improve future recommendations.</p></div>}

              <div className="grid gap-4 sm:grid-cols-2"><div className="space-y-2"><Label>Reference</Label><Input value={expenseForm.reference} onChange={(e) => setExpenseForm({ ...expenseForm, reference: e.target.value })} placeholder="Invoice / receipt number" /></div><div className="space-y-2"><Label>Notes</Label><Input value={expenseForm.notes} onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })} placeholder="Optional internal note" /></div></div>

              <label className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm"><input type="checkbox" className="mt-1" checked={expenseForm.allow_duplicate} onChange={(e) => setExpenseForm({ ...expenseForm, allow_duplicate: e.target.checked })} /><span><strong>Allow an intentional duplicate.</strong><br /><span className="text-slate-500">Use only when two genuine purchases have the same date, amount, vendor, and description.</span></span></label>

              {expenseForm.accounting_pathway === 'quick_expense' && <div className="rounded-lg bg-slate-900 p-3 text-sm text-white"><strong>Final posting:</strong><br />• Debit the selected expense account<br />• Credit the selected business bank/cash account<br />• Update General Ledger, cash flow, account balance, and audit metadata once</div>}
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => { setActiveModal(null); resetExpenseForm(); }}>Cancel</Button><Button type="submit" disabled={submitting || suggestingExpense || !expenseSuggestion} className={expenseForm.accounting_pathway === 'quick_expense' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}>{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{!expenseSuggestion ? 'Get Recommendation First' : expenseForm.accounting_pathway === 'quick_expense' ? 'Confirm and Post Expense' : `Open ${pathwayLabel(expenseForm.accounting_pathway)}`}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Salary Payment Modal */}
      <Dialog open={activeModal === 'salary'} onOpenChange={() => setActiveModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Pay Salary
            </DialogTitle>
            <DialogDescription>
              Record employee salary payment
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSalarySubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Employee Name *</Label>
                <Input
                  value={salaryForm.employee_name}
                  onChange={(e) => setSalaryForm({ ...salaryForm, employee_name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Basic Salary (LKR) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={salaryForm.amount}
                    onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })}
                    placeholder="Basic salary"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>For Month *</Label>
                  <Input
                    value={salaryForm.month}
                    onChange={(e) => setSalaryForm({ ...salaryForm, month: e.target.value })}
                    placeholder="e.g., January 2026"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pay From Account *</Label>
                  <Select
                    value={salaryForm.bank_account_id}
                    onValueChange={(value) => { const account = bankAccounts.find((item) => item.id === value); setSalaryForm({ ...salaryForm, bank_account_id: value, payment_method: account?.account_type === 'cash' ? 'cash' : 'bank' }); }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.account_name} ({formatCurrency(acc.current_balance)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    max={getTodayDate()}
                    value={salaryForm.date}
                    onChange={(e) => setSalaryForm({ ...salaryForm, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Allowances</Label>
                  <Input
                    type="number"
                    min="0"
                    value={salaryForm.allowances}
                    onChange={(e) => setSalaryForm({ ...salaryForm, allowances: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deductions</Label>
                  <Input
                    type="number"
                    min="0"
                    value={salaryForm.deductions}
                    onChange={(e) => setSalaryForm({ ...salaryForm, deductions: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700">
                  {salaryForm.payment_method === 'cash' ? 'Cash' : 'Bank Transfer'}
                </div>
              </div>
              {salaryForm.amount && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Basic Salary:</span>
                    <span>{formatCurrency(parseFloat(salaryForm.amount) || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>+ Allowances:</span>
                    <span>{formatCurrency(parseFloat(salaryForm.allowances) || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>- Deductions:</span>
                    <span>{formatCurrency(parseFloat(salaryForm.deductions) || 0)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-semibold">
                    <span>Net Pay:</span>
                    <span>{formatCurrency(
                      (parseFloat(salaryForm.amount) || 0) + 
                      (parseFloat(salaryForm.allowances) || 0) - 
                      (parseFloat(salaryForm.deductions) || 0)
                    )}</span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Pay Salary
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Revenue Receipt Modal */}
      <Dialog open={activeModal === 'revenue'} onOpenChange={() => setActiveModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Receive Payment
            </DialogTitle>
            <DialogDescription>
              Record income or payment received
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRevenueSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Income Type *</Label>
                <Select
                  value={revenueForm.revenue_type}
                  onValueChange={(value) => setRevenueForm({ ...revenueForm, revenue_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={revenueForm.description}
                  onChange={(e) => setRevenueForm({ ...revenueForm, description: e.target.value })}
                  placeholder="What is this payment for?"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (LKR) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={revenueForm.amount}
                    onChange={(e) => setRevenueForm({ ...revenueForm, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deposit To Account *</Label>
                  <Select
                    value={revenueForm.bank_account_id}
                    onValueChange={(value) => { const account = bankAccounts.find((item) => item.id === value); setRevenueForm({ ...revenueForm, bank_account_id: value, payment_method: account?.account_type === 'cash' ? 'cash' : 'bank' }); }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.account_name} ({formatCurrency(acc.current_balance)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Receipt Date *</Label>
                  <Input
                    type="date"
                    max={getTodayDate()}
                    value={revenueForm.date}
                    onChange={(e) => setRevenueForm({ ...revenueForm, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700">
                    {revenueForm.payment_method === 'cash' ? 'Cash' : 'Bank Transfer'}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Customer/Payer</Label>
                <Input
                  value={revenueForm.customer}
                  onChange={(e) => setRevenueForm({ ...revenueForm, customer: e.target.value })}
                  placeholder="Who paid?"
                />
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
                <strong>This will:</strong><br />
                • Increase selected account balance<br />
                • Record as business income
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Loan Transaction Modal */}
      <Dialog open={activeModal === 'loan'} onOpenChange={() => setActiveModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-purple-600" />
              Loan Transaction
            </DialogTitle>
            <DialogDescription>
              Record loan received or repayment
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLoanSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Transaction Type *</Label>
                  <Select
                    value={loanForm.transaction_type}
                    onValueChange={(value) => setLoanForm({ ...loanForm, transaction_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receive">Receive Loan</SelectItem>
                      <SelectItem value="repay">Repay Loan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Loan Type *</Label>
                  <Select
                    value={loanForm.loan_type}
                    onValueChange={(value) => setLoanForm({ ...loanForm, loan_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_loan">Bank Loan</SelectItem>
                      <SelectItem value="finance_company">Finance Company Loan</SelectItem>
                      <SelectItem value="director_loan">Director Loan</SelectItem>
                      <SelectItem value="other">Other Loan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lender Name *</Label>
                  <Input
                    value={loanForm.lender_name}
                    onChange={(e) => setLoanForm({ ...loanForm, lender_name: e.target.value })}
                    placeholder="Bank name or person"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{loanForm.transaction_type === 'receive' ? 'Deposit To' : 'Pay From'} Account *</Label>
                  <Select
                    value={loanForm.bank_account_id}
                    onValueChange={(value) => setLoanForm({ ...loanForm, bank_account_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.account_name} ({formatCurrency(acc.current_balance)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Principal Amount (LKR) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={loanForm.amount}
                    onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })}
                    placeholder="Loan amount"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Transaction Date *</Label>
                  <Input
                    type="date"
                    max={getTodayDate()}
                    value={loanForm.date}
                    onChange={(e) => setLoanForm({ ...loanForm, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              {loanForm.transaction_type === 'repay' && (
                <div className="space-y-2">
                  <Label>Interest Amount</Label>
                  <Input
                    type="number"
                    min="0"
                    value={loanForm.interest_amount}
                    onChange={(e) => setLoanForm({ ...loanForm, interest_amount: e.target.value })}
                    placeholder="0"
                  />
                </div>
              )}
              <div className={`p-3 rounded-lg text-sm ${
                loanForm.transaction_type === 'receive' ? 'bg-purple-50 text-purple-800' : 'bg-orange-50 text-orange-800'
              }`}>
                {loanForm.transaction_type === 'receive' ? (
                  <>
                    <strong>Loan Received will:</strong><br />
                    • Increase selected account balance<br />
                    • Create loan liability
                  </>
                ) : (
                  <>
                    <strong>Loan Repayment will:</strong><br />
                    • Reduce Cash/Bank balance<br />
                    • Reduce loan liability<br />
                    {loanForm.interest_amount && parseFloat(loanForm.interest_amount) > 0 && (
                      <>• Record interest expense</>
                    )}
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={submitting} 
                className={loanForm.transaction_type === 'receive' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-600 hover:bg-orange-700'}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {loanForm.transaction_type === 'receive' ? 'Record Loan' : 'Record Repayment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction Correction Request */}
      <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Request Transaction Correction</DialogTitle>
            <DialogDescription>
              Approved corrections reverse the original journal and post a linked replacement. Amounts and accounts cannot be changed here.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Corrected date</Label><Input type="date" max={getTodayDate()} value={correctionForm.date} onChange={(e) => setCorrectionForm({ ...correctionForm, date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Reference</Label><Input value={correctionForm.reference_number} onChange={(e) => setCorrectionForm({ ...correctionForm, reference_number: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={correctionForm.description} onChange={(e) => setCorrectionForm({ ...correctionForm, description: e.target.value })} /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={correctionForm.notes} onChange={(e) => setCorrectionForm({ ...correctionForm, notes: e.target.value })} /></div>
            <div className="space-y-2"><Label>Reason for correction *</Label><Textarea value={correctionForm.reason} onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })} placeholder="Explain what was entered incorrectly" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionOpen(false)}>Cancel</Button>
            <Button onClick={submitCorrection} disabled={submitting || !correctionForm.reason.trim()}>
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Send for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Reverse Transaction
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reverse this transaction?
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{transactionToDelete?.description}</p>
                <p className="text-sm text-slate-600">
                  Amount: {transactionToDelete && formatCurrency(transactionToDelete.total_debit)}
                </p>
                <p className="text-sm text-slate-500">
                  Entry: {transactionToDelete?.entry_number}
                </p>
              </div>
              <p className="mt-3 text-sm text-red-600">
                <strong>Warning:</strong> This will reverse all associated journal entries and update account balances. This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTransaction}
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Reverse Transaction
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excel Upload Dialog */}
      <Dialog open={excelUploadOpen} onOpenChange={(open) => !open && closeExcelUpload()}>
        <DialogContent className="max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="excel-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              {uploadStep === 'success' ? 'Import Complete' : 'Import Expenses from Excel'}
            </DialogTitle>
            <DialogDescription>
              {uploadStep === 'upload' && 'Upload an Excel file to import multiple expense transactions'}
              {uploadStep === 'validating' && 'Validating your file...'}
              {uploadStep === 'preview' && 'Review and edit transactions before importing'}
              {uploadStep === 'importing' && 'Importing transactions...'}
              {uploadStep === 'success' && `Successfully imported ${importResult?.imported_count} transactions`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {/* Upload Step */}
            {uploadStep === 'upload' && (
              <div className="space-y-4 py-4">
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-700">Drop your Excel file here</p>
                  <p className="text-sm text-slate-500 mt-1">or click to browse</p>
                  <p className="text-xs text-slate-400 mt-2">Only .xlsx files are supported</p>
                </div>

                <div className="flex items-center justify-center gap-4 text-sm">
                  <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Template Instructions:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Download the template and fill in your expense data</li>
                    <li>• Required fields: Expense Type, Description, Amount, Pay From, Transaction Date</li>
                    <li>• Bank account names must match exactly with your system accounts</li>
                    <li>• Use the dropdown for Expense Type to ensure valid values</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Validating Step */}
            {uploadStep === 'validating' && (
              <div className="py-12 text-center">
                <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-700">Validating your file...</p>
                <p className="text-sm text-slate-500 mt-1">Checking for errors and duplicates</p>
              </div>
            )}

            {/* Preview Step */}
            {uploadStep === 'preview' && validationResult && (
              <div className="space-y-4 py-4">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-800">{validationResult.total_rows}</p>
                    <p className="text-sm text-slate-500">Total Rows</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{validationResult.valid_rows}</p>
                    <p className="text-sm text-emerald-600">Valid</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{validationResult.invalid_rows}</p>
                    <p className="text-sm text-red-600">Invalid</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{validationResult.duplicate_rows}</p>
                    <p className="text-sm text-amber-600">Duplicates</p>
                  </div>
                </div>

                {/* Error Warning */}
                {!validationResult.can_import && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Cannot import - please fix all errors first</p>
                      <p className="text-sm text-red-600">Edit or remove invalid rows before importing</p>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <div className="border rounded-lg overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2 w-10">#</th>
                        <th className="text-left p-2 w-8">Status</th>
                        <th className="text-left p-2 min-w-[120px]">Expense Type</th>
                        <th className="text-left p-2 min-w-[150px]">Record To Account</th>
                        <th className="text-left p-2 min-w-[180px]">Description</th>
                        <th className="text-right p-2 min-w-[100px]">Amount</th>
                        <th className="text-left p-2 min-w-[130px]">Pay From</th>
                        <th className="text-left p-2 min-w-[100px]">Date</th>
                        <th className="text-left p-2 min-w-[100px]">Vendor</th>
                        <th className="text-left p-2 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {validationResult.rows.map((row, idx) => (
                        <tr 
                          key={idx} 
                          className={`${!row.is_valid ? 'bg-red-50' : row.is_duplicate ? 'bg-amber-50' : ''}`}
                        >
                          <td className="p-2 text-slate-500">{row.row_number}</td>
                          <td className="p-2">
                            {row.is_valid && !row.is_duplicate && (
                              <Check className="w-4 h-4 text-emerald-600" />
                            )}
                            {!row.is_valid && (
                              <div className="relative group">
                                <X className="w-4 h-4 text-red-600" />
                                <div className="absolute left-0 top-6 z-10 hidden group-hover:block bg-red-800 text-white text-xs rounded p-2 w-48">
                                  {row.errors.join(', ')}
                                </div>
                              </div>
                            )}
                            {row.is_valid && row.is_duplicate && (
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                            )}
                          </td>
                          <td className="p-2">
                            {editingRow === idx ? (
                              <Select 
                                value={row.expense_type || ''} 
                                onValueChange={(v) => handleUpdateRow(idx, 'expense_type', v)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {expenseTypes.map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="capitalize">{row.expense_type?.replace('_', ' ')}</span>
                            )}
                          </td>
                          <td className="p-2">
                            {editingRow === idx ? (
                              <Select 
                                value={row.expense_account_code || 'auto'} 
                                onValueChange={(v) => handleUpdateRow(idx, 'expense_account_code', v === 'auto' ? null : v)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue placeholder="Auto" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[400px]">
                                  <SelectItem value="auto">Auto (based on type)</SelectItem>
                                  {validationResult.expense_accounts?.map(ea => (
                                    ea.is_header ? (
                                      <div
                                        key={ea.code}
                                        className="px-2 py-1.5 mt-1 text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-100 border-y border-slate-200 select-none cursor-default"
                                      >
                                        {ea.code} - {ea.name}
                                      </div>
                                    ) : (
                                      <SelectItem key={ea.code} value={ea.code} className="pl-6">
                                        <span className="text-slate-400 mr-1">›</span>
                                        {ea.code} - {ea.name}
                                      </SelectItem>
                                    )
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="truncate block max-w-[150px]" title={row.expense_account_display || 'Auto'}>
                                {row.expense_account_code ? `${row.expense_account_code}` : <span className="text-slate-400">Auto</span>}
                              </span>
                            )}
                          </td>
                          <td className="p-2">
                            {editingRow === idx ? (
                              <Input 
                                value={row.description || ''} 
                                onChange={(e) => handleUpdateRow(idx, 'description', e.target.value)}
                                className="h-8"
                              />
                            ) : (
                              <span className="truncate block max-w-[180px]" title={row.description}>{row.description}</span>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            {editingRow === idx ? (
                              <Input 
                                type="number"
                                value={row.amount || ''} 
                                onChange={(e) => handleUpdateRow(idx, 'amount', parseFloat(e.target.value) || 0)}
                                className="h-8 w-24 text-right"
                              />
                            ) : (
                              formatCurrency(row.amount || 0)
                            )}
                          </td>
                          <td className="p-2">
                            {editingRow === idx ? (
                              <Select 
                                value={row.bank_account_id || ''} 
                                onValueChange={(v) => handleUpdateRow(idx, 'bank_account_id', v)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {validationResult.bank_accounts?.map(ba => (
                                    <SelectItem key={ba.id} value={ba.id}>{ba.account_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="truncate block max-w-[130px]">{row.bank_account_name}</span>
                            )}
                          </td>
                          <td className="p-2">
                            {editingRow === idx ? (
                              <Input 
                                type="date"
                    max={getTodayDate()}
                                value={row.date || ''} 
                                onChange={(e) => handleUpdateRow(idx, 'date', e.target.value)}
                                className="h-8"
                              />
                            ) : (
                              row.date
                            )}
                          </td>
                          <td className="p-2">
                            <span className="truncate block max-w-[100px]" title={row.vendor}>{row.vendor || '-'}</span>
                          </td>
                          <td className="p-2">
                            <div className="flex gap-1">
                              {editingRow === idx ? (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => setEditingRow(null)}
                                  className="h-7 w-7 p-0"
                                >
                                  <Check className="w-4 h-4 text-emerald-600" />
                                </Button>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleEditRow(idx)}
                                  className="h-7 w-7 p-0"
                                >
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleRemoveRow(idx)}
                                className="h-7 w-7 p-0"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Importing Step */}
            {uploadStep === 'importing' && (
              <div className="py-12 text-center">
                <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-700">Importing transactions...</p>
                <p className="text-sm text-slate-500 mt-1">Please wait, this may take a moment</p>
              </div>
            )}

            {/* Success Step */}
            {uploadStep === 'success' && importResult && (
              <div className="py-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <p className="text-xl font-semibold text-slate-800 mb-2">Import Successful!</p>
                <p className="text-slate-600 mb-6">
                  {importResult.imported_count} expense transactions have been imported and recorded.
                </p>
                <div className="bg-slate-50 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-sm text-slate-500 mb-2">Imported transactions are tagged as "Excel Import" and can be found in the Recent Transactions list.</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            {uploadStep === 'upload' && (
              <Button variant="outline" onClick={closeExcelUpload}>Cancel</Button>
            )}
            {uploadStep === 'preview' && (
              <>
                <Button variant="outline" onClick={resetExcelUpload}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Upload Different File
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleImportExpenses}
                  disabled={!validationResult?.can_import || importing}
                  data-testid="confirm-import-btn"
                >
                  {importing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Import {validationResult?.valid_rows} Transactions
                </Button>
              </>
            )}
            {uploadStep === 'success' && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={closeExcelUpload}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
